import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { urls } from 'app-constants';
import PropTypes from 'prop-types';
import camelize from 'camelize';
import queryString from 'query-string';

const MediaDataCacheContext = createContext();

export const MediaDataCacheProvider = ({ children }) => {
  const [contextVal, setContextVal] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchingIds, setFetchingIds] = useState([]);
  const fetchingPromises = useRef({});
  const startFetching = ids => setFetchingIds(os => [...os, ...ids.filter(val => !os.includes(val))]);
  const stopFetching = ids => setFetchingIds(os => os.filter(val => !ids.includes(val)));

  const updateMediaDataCache = val => setContextVal(prevState => ({ ...prevState, ...val }));
  const value = {
    mediaDataCache: contextVal,
    setMediaDataCache: setContextVal,
    updateMediaDataCache,
    hasLoaded,
    setHasLoaded,
    fetchingIds,
    fetchingPromises,
    startFetching,
    stopFetching,
  };
  return (
    <MediaDataCacheContext.Provider value={value}>
      {children}
    </MediaDataCacheContext.Provider>
  );
};

MediaDataCacheProvider.propTypes = {
  children: PropTypes.any,
};

export function useMediaDataCache (
  initialValues = [],
  { keys = 'uuid', excludeFields = ['capture_date_components', 'thumbnail_lg'] } = {},
) {
  // const  = opts;
  const context = useContext(MediaDataCacheContext);
  if (typeof context === 'undefined') return null;
  const {
    mediaDataCache,
    updateMediaDataCache,
    hasLoaded,
    setHasLoaded,
    fetchingIds,
    fetchingPromises,
    startFetching,
    stopFetching,
  } = context;


  const fetchItems = (ids = []) => {
    if (!ids.length > 0) return null;
    const params = { keys, exclude_fields: excludeFields, ids };
    startFetching(ids);
    const p = fetch(`${urls.mediaBulkData}?${queryString.stringify(params)}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Error retrieving media details.');
        }
        return response;
      })
      .then(response => response.json())
      .then(data => {
        const missingIds = ids.filter(val => !data.hasOwnProperty(val));
        // camelize values only
        const camelizedData = Object.entries(data).reduce((result, [key, val]) => ({ ...result, [key]: camelize(val) }), {});
        updateMediaDataCache({
          ...camelizedData,
          ...missingIds.reduce((result, id) => ({ ...result, [id]: null }), {}),
        });
        setHasLoaded(true);
        stopFetching(ids);
        ids.forEach(id => delete fetchingPromises.current[id]);
        return data;
      })
      .catch(err => {
        console.error(err);
        stopFetching(ids);
      });

    ids.forEach(id => fetchingPromises.current[id] = p);
    return p;
  };

  useEffect(() => {
    fetchItems(initialValues.filter(v => !fetchingIds.includes(v) && !mediaDataCache.hasOwnProperty(v)));
  }, [JSON.stringify(initialValues)]);

  const get = useCallback(id => {
    if (!id) return Promise.resolve(null);
    if (mediaDataCache.hasOwnProperty(id)) {
      return Promise.resolve(mediaDataCache[id]);
    } else {
      const promise = fetchingPromises.current[id] || fetchItems([id]);
      return promise.then(data => data[id]);
    }
  }, [JSON.stringify(mediaDataCache), JSON.stringify(fetchingIds)]);

  return { get, ready: hasLoaded };
}

export function useCachedMedia (id) {
  const cache = useMediaDataCache();
  if (cache === null) return undefined;
  const [state, setState] = useState(null);
  useEffect(() => {
    if (id) {
      cache.get(id).then(setState);
    } else {
      setState(null);
    }
  }, [id]);
  return state;
}

export function withMediaDataCache (WrappedComponent) {
  const Component = props => (
    <MediaDataCacheProvider>
      <WrappedComponent {...props} />
    </MediaDataCacheProvider>
  );

  Component.displayName = `withMediaDataCache(${WrappedComponent.displayName || WrappedComponent.name})`;
  return Component;
}
